<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PACKAGE_COMPONENT_TYPES } from '../bookingTypes';
import FormField from './FormField.vue';
import BookingTypeFields from '../BookingTypeFields.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.PACKAGE';

const components = () => props.modelValue.components || [];

const set = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

// Local mirror of the component list, updated before each emit. Several
// children (every `FlightFields`) emit their defaults in the same mount flush,
// when `props.modelValue` still holds the pre-first-emit value; building from
// the mirror makes those emits compose instead of clobbering each other.
// Seeded once from the initial modelValue; the parent must `:key` this
// component by booking id so switching bookings remounts it.
const latest = ref(components());

// Stable render keys: the field components seed local state at setup, so
// removing a component must remount its editor instead of handing a
// neighbour's data to the reused instance.
let lastKey = 0;
const nextKey = () => {
  lastKey += 1;
  return lastKey;
};
const keys = ref(latest.value.map(() => nextKey()));

watch(
  () => props.modelValue.components,
  list => {
    latest.value = list || [];
  }
);

const setComponents = list => {
  latest.value = list;
  set({ components: list });
};

const detailsOf = component => {
  const { type, ...details } = component;
  return details;
};

const typeOptions = PACKAGE_COMPONENT_TYPES.map(type => ({
  value: type.value,
  label: t(type.labelKey),
}));

const addComponent = () => {
  keys.value = [...keys.value, nextKey()];
  setComponents([...latest.value, { type: PACKAGE_COMPONENT_TYPES[0].value }]);
};

const removeComponent = index => {
  keys.value = keys.value.filter((_, itemIndex) => itemIndex !== index);
  setComponents(latest.value.filter((_, itemIndex) => itemIndex !== index));
};

// Changing the type resets that component's fields; they would not carry over.
const setType = (index, type) =>
  setComponents(
    latest.value.map((component, itemIndex) =>
      itemIndex === index ? { type } : component
    )
  );

const setDetails = (index, details) =>
  setComponents(
    latest.value.map((component, itemIndex) =>
      itemIndex === index ? { ...details, type: component.type } : component
    )
  );

const errorsFor = index =>
  Object.fromEntries(
    Object.entries(props.errors)
      .filter(([path]) => path.startsWith(`components.${index}.`))
      .map(([path, message]) => [
        path.slice(`components.${index}.`.length),
        message,
      ])
  );
</script>

<template>
  <div class="flex flex-col gap-4">
    <FormField
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PACKAGE_COMPONENTS')"
      :error="errors.components"
    >
      <p v-if="!components().length" class="text-sm text-n-slate-11">
        {{ t(`${KEY}.EMPTY`) }}
      </p>
      <div
        v-for="(component, index) in components()"
        :key="keys[index]"
        class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="flex items-end justify-between gap-2">
          <FormField :label="t(`${KEY}.COMPONENT`, { index: index + 1 })">
            <Select
              :model-value="component.type"
              :options="typeOptions"
              @update:model-value="setType(index, $event)"
            />
          </FormField>
          <Button
            ghost
            xs
            ruby
            icon="i-lucide-trash-2"
            :label="t(`${KEY}.REMOVE_COMPONENT`)"
            @click="removeComponent(index)"
          />
        </div>
        <BookingTypeFields
          :key="component.type"
          :booking-type="component.type"
          :model-value="detailsOf(component)"
          :errors="errorsFor(index)"
          @update:model-value="setDetails(index, $event)"
        />
      </div>
      <Button
        ghost
        xs
        icon="i-lucide-plus"
        class="self-start"
        :label="t(`${KEY}.ADD_COMPONENT`)"
        @click="addComponent"
      />
    </FormField>

    <TextArea
      :model-value="modelValue.notes"
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES')"
      :placeholder="
        t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES_PLACEHOLDER')
      "
      :max-length="2000"
      @update:model-value="set({ notes: $event })"
    />
  </div>
</template>
